import { memo } from 'react';
import { ReactComponent as ChevronIcon } from '@assets/icons/chevron-right.svg';
import { ReactComponent as Dots } from '@assets/icons/ellipsis-vertical.svg';
import config from 'config';
import Link from 'next/link';

import {
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList
} from '@chakra-ui/react';

const {
  colors: {
    palette: {
      dashboard: {
        dottedMenuButton: {
          background,
          borderBoxColor,
          cancelBorder,
          labelColor,
          iconColor
        }
      }
    }
  }
} = config;

const DASHBOARD_ACTION_COLUMN_WIDTH = '20px';

interface DottedMenuButtonProps {
  detailsLink: string;
  viewLabel?: string;
  useActionsMenu?: boolean;
  hideViewMenuItem?: boolean;
  isRescheduleAllow?: boolean;
  isRepeatAllow?: boolean;
  isCancelAllow?: boolean;
  isArchiveAllow?: boolean;
  archiveLabel?: string;
  onReschedule?: () => void;
  onRepeat?: () => void;
  onCancel?: () => void;
  onArchive?: () => void;
  alignToContentEdge?: boolean;
}

const DottedMenuButton = ({
  detailsLink,
  viewLabel = 'Details',
  useActionsMenu = false,
  hideViewMenuItem = false,
  isRescheduleAllow = false,
  isRepeatAllow = false,
  isCancelAllow = false,
  isArchiveAllow = false,
  archiveLabel = 'Archive',
  onReschedule = () => {},
  onRepeat = () => {},
  onCancel = () => {},
  onArchive = () => {},
  alignToContentEdge = false
}: DottedMenuButtonProps) => {
  const showActionsMenu =
    useActionsMenu ||
    isRescheduleAllow ||
    isRepeatAllow ||
    isCancelAllow ||
    isArchiveAllow;

  return (
    <Menu
      placement='left-start'
      gutter={0}
      modifiers={[
        {
          name: 'offset',
          options: { offset: [0, 0] }
        },
        {
          name: 'flip',
          enabled: false
        },
        {
          name: 'preventOverflow',
          enabled: false
        }
      ]}
    >
      {!showActionsMenu ? (
        <IconButton
          as={Link}
          href={detailsLink}
          bg='transparent'
          aria-label='open appointment details'
          minW={alignToContentEdge ? DASHBOARD_ACTION_COLUMN_WIDTH : undefined}
          w={alignToContentEdge ? DASHBOARD_ACTION_COLUMN_WIDTH : undefined}
          h={alignToContentEdge ? DASHBOARD_ACTION_COLUMN_WIDTH : undefined}
          p={alignToContentEdge ? 0 : undefined}
          icon={<ChevronIcon fill={iconColor} width={10} height={16} />}
        />
      ) : (
        <MenuButton
          as={IconButton}
          minW={alignToContentEdge ? DASHBOARD_ACTION_COLUMN_WIDTH : 'auto'}
          w={alignToContentEdge ? DASHBOARD_ACTION_COLUMN_WIDTH : undefined}
          h={alignToContentEdge ? DASHBOARD_ACTION_COLUMN_WIDTH : undefined}
          bg='transparent'
          borderRadius='2px'
          _active={{ bg: background, borderLeftRadius: 0 }}
          _hover={{ bg: background }}
          p={alignToContentEdge ? 0 : 2}
          mr={alignToContentEdge ? 0 : 1}
          aria-label='open actions menu'
        >
          <Dots fill={iconColor} width={20} height={20} />
        </MenuButton>
      )}

      <MenuList
        borderColor={borderBoxColor}
        borderWidth='1px'
        borderRadius={0}
        p={0}
        minW='180px'
        overflow='hidden'
      >
        <MenuItem
          as={Link}
          href={detailsLink}
          height='39px'
          display={hideViewMenuItem ? 'none' : undefined}
        >
          {viewLabel}
        </MenuItem>

        {isRescheduleAllow && (
          <MenuItem color={labelColor} height='39px' onClick={onReschedule}>
            Reschedule
          </MenuItem>
        )}
        {isRepeatAllow && (
          <MenuItem color={labelColor} height='39px' onClick={onRepeat}>
            Repeat
          </MenuItem>
        )}
        {isArchiveAllow && (
          <MenuItem color={labelColor} height='39px' onClick={onArchive}>
            {archiveLabel}
          </MenuItem>
        )}
        {isCancelAllow && (
          <MenuItem
            color={labelColor}
            height='39px'
            borderTopWidth='0.5px'
            borderTopColor={cancelBorder}
            onClick={onCancel}
          >
            Cancel
          </MenuItem>
        )}
      </MenuList>
    </Menu>
  );
};

export default memo(DottedMenuButton);
